import { Palette, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { useEffect, useState, memo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useDeleteInstance } from '@/hooks/useDeleteInstance';
import { useCreateInstance } from '@/hooks/useCreateInstance';
import { useFetchInstance } from '@/hooks/useFetchInstance';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useLoginComfyUIRoute } from '@/hooks/useLoginComfyUI';
import { dockerImagesAtom, dockerImagesLoadingAtom, getAtomsForTier } from '@/hooks/atoms';
import { UserRole } from '@/hooks/useUserAppRoles';

const capitalize = (str?: string) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const formatSeconds = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
};

interface InstanceCardProps {
  tier?: string;
  roles: UserRole[];
}

export const InstanceCard = memo(function InstanceCard({ tier = 'standard', roles = [] }: InstanceCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBuildVersion, setSelectedBuildVersion] = useState<string>('latest');

  // Get tier-specific atoms
  const atoms = getAtomsForTier(tier);
  const [currentInstance] = useAtom(atoms.instanceAtom);
  const [loadingState] = useAtom(atoms.loadingAtom);

  // Docker images atoms
  const images = useAtomValue(dockerImagesAtom);
  const imagesLoading = useAtomValue(dockerImagesLoadingAtom);

  // Get tier-specific hooks
  const { fetchInstance } = useFetchInstance(tier);
  const { createInstance } = useCreateInstance(tier);
  const { deleteInstance } = useDeleteInstance(tier);
  const { getLoggedInComfyUIRoute } = useLoginComfyUIRoute();

  // Check if user has maintainer role
  const hasMaintainerRole = roles?.some((role) => role.role_key === 'maintainer');

  // Note: 'restarting' cannot occur with restartPolicy: Never — liveness probe → pod Failed
  const isStuck = currentInstance?.status === 'stuck';
  const isFailed = currentInstance?.status === 'failed';
  const isUnhealthy = isStuck || isFailed;

  useEffect(() => {
    // Initial fetch
    fetchInstance();

    // Poll more frequently when stuck or restarting so we catch recovery quickly
    const interval = setInterval(
      () => {
        fetchInstance();
      },
      isUnhealthy ? 15000 : 30000,
    );

    return () => clearInterval(interval);
  }, [fetchInstance, isUnhealthy]);

  const handleConfirmModal = async () => {
    deleteInstance();
    setIsModalOpen(false);
  };

  const handleOpenApp = async () => {
    const url = await getLoggedInComfyUIRoute(tier);
    if (url) {
      const newTab = window.open(url, '_blank');
      if (newTab) {
        newTab.focus();
      }
    }
  };

  return (
    <div>
      <Card
        className="dark:shadow-[0_0_15px_rgba(102,126,234,0.3)] transition-shadow duration-300 
          dark:hover:shadow-[0_0_25px_rgba(102,126,234,0.4)]"
      >
        <CardHeader>
          <CardTitle className="flex justify-between">
            <div className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              {`${tier === 'standard' ? 'Lightweight AI workflow' : 'Heavyweight AI workflow'}`}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-neutral-400">Manage your instance</p>
          {loadingState.fetch && currentInstance === null ? (
            <div className="flex items-center">
              <Spinner className="mr-2 h-4 w-4" />
              <span className="text-sm text-slate-600 dark:text-neutral-400">Loading...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {/* ── Stuck alert ─────────────────────────────────────── */}
              {isStuck && (
                <Alert
                  variant="destructive"
                  className="border-amber-500 bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-600"
                >
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <AlertTitle className="font-semibold">Instance Startup Issue</AlertTitle>
                  <AlertDescription className="space-y-1">
                    <p>
                      Your instance has been running for{' '}
                      <span className="font-medium">{formatSeconds(currentInstance.stuck_info?.seconds_since_start ?? 0)}</span>{' '}
                      but port 8188 is not responding. A custom node or startup script may be stuck.
                    </p>
                    <p className="text-sm font-medium mt-1">Please terminate and start a new instance.</p>
                  </AlertDescription>
                </Alert>
              )}

              {/* ── Failed alert (liveness killed the container) ─────── */}
              {isFailed && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertTitle className="font-semibold">Instance Failed</AlertTitle>
                  <AlertDescription className="space-y-1">
                    <p>
                      The instance encountered an error and could not start.
                      {currentInstance?.stuck_info?.last_exit_reason && (
                        <span className="ml-1">
                          Reason: <span className="font-mono font-medium">{currentInstance.stuck_info.last_exit_reason}</span>
                          {currentInstance.stuck_info.last_exit_code !== undefined && (
                            <span className="ml-1">(exit code {currentInstance.stuck_info.last_exit_code})</span>
                          )}
                        </span>
                      )}
                    </p>
                    <p className="text-sm font-medium">Please terminate and start a new instance.</p>
                  </AlertDescription>
                </Alert>
              )}

              {/* ── Normal starting alert ────────────────────────────── */}
              {Boolean(currentInstance?.status) === true && currentInstance?.status !== 'ready' && !isUnhealthy && (
                <Alert>
                  <Spinner className="mr-2 h-4 w-4" />
                  <AlertTitle>{`${capitalize(currentInstance?.status || '')} Instance`}</AlertTitle>
                  <AlertDescription>We are setting up your environment. This usually takes a few minutes.</AlertDescription>
                </Alert>
              )}

              {/* ── Ready alert ──────────────────────────────────────── */}
              {currentInstance?.status === 'ready' && (
                <Alert>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  <AlertTitle>Instance Ready</AlertTitle>
                  <AlertDescription>Click the open button to redirect to your environment.</AlertDescription>
                </Alert>
              )}

              {/* ── Build version selector (maintainer, no active instance) ── */}
              {hasMaintainerRole && Boolean(currentInstance?.status) === false && (
                <div className="space-y-2">
                  <label htmlFor="build-version" className="text-sm font-medium text-slate-700 dark:text-neutral-300">
                    Build Version
                  </label>
                  <Select value={selectedBuildVersion} onValueChange={setSelectedBuildVersion} disabled={imagesLoading}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select build version" />
                    </SelectTrigger>
                    <SelectContent className="max-h-48 overflow-y-auto">
                      <SelectItem value="latest">Latest</SelectItem>
                      {images.map((image) => (
                        <SelectItem key={image.tag} value={image.tag}>
                          {image.tag} {image.isLatest ? '(Latest)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {imagesLoading && (
                    <div className="flex items-center text-sm text-slate-500">
                      <Spinner className="mr-2 h-3 w-3" />
                      Loading build versions...
                    </div>
                  )}
                </div>
              )}

              {/* ── Action buttons ───────────────────────────────────── */}
              <div className="flex justify-between space-x-2">
                <div className="flex gap-2">
                  {Boolean(currentInstance?.status) === false && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => createInstance(hasMaintainerRole ? selectedBuildVersion : undefined)}
                      disabled={loadingState.create}
                      className="bg-gradient-to-r from-[#667eea] to-[#764ba2] hover:from-[#5568d3] hover:to-[#6a4190] border-0"
                    >
                      {loadingState.create && <Spinner className="mr-2 h-4 w-4" />}
                      Start
                    </Button>
                  )}
                  {/* Show Terminate for any active instance — prominently for stuck/restarting */}
                  {Boolean(currentInstance?.status) === true && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setIsModalOpen(true)}
                      disabled={loadingState.delete}
                      className={isUnhealthy ? 'ring-2 ring-red-400 ring-offset-1' : ''}
                    >
                      {loadingState.delete && <Spinner className="mr-2 h-4 w-4" />}
                      Terminate
                    </Button>
                  )}
                </div>
                {currentInstance?.status === 'ready' && (
                  <Button variant="outline" size="sm" className="flex gap-2" onClick={handleOpenApp}>
                    Open
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmModal}
        title="Terminate instance"
        description={
          isStuck
            ? 'The instance appears to be stuck. Terminating it will let you start fresh.'
            : 'Are you sure you want to terminate this instance?'
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="destructive"
      />
    </div>
  );
});
